'use client';

import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';

import { cn } from '@/hen-ui/utils';
import { usePortalContainer } from '@/hen-ui/providers/PortalContainerContext';
import {
  DIM_BACKDROP_CLASS,
  isContainedPreviewPortal,
} from '@/hen-ui/shadcn/dialog';

function Popover({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot='popover' {...props} />;
}

const PopoverTrigger = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger>
>(({ ...props }, ref) => (
  <PopoverPrimitive.Trigger data-slot='popover-trigger' ref={ref} {...props} />
));

PopoverTrigger.displayName = PopoverPrimitive.Trigger.displayName;

const PopoverAnchor = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Anchor>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Anchor>
>(({ ...props }, ref) => (
  <PopoverPrimitive.Anchor data-slot='popover-anchor' ref={ref} {...props} />
));

PopoverAnchor.displayName = PopoverPrimitive.Anchor.displayName;

/**
 * Portals into the provided container, else into the body of the document the
 * popover renders in. Radix would otherwise use the top-level document, and a
 * popover inside an iframe would then miss every outside press in that iframe.
 */
function PopoverPortal({
  forceMount,
  children,
}: {
  forceMount?: true;
  children: React.ReactNode;
}) {
  const portalContainer = usePortalContainer();
  const markerRef = React.useRef<HTMLSpanElement>(null);
  const [ownerBody, setOwnerBody] = React.useState<HTMLElement | undefined>();

  React.useLayoutEffect(() => {
    setOwnerBody(markerRef.current?.ownerDocument?.body ?? undefined);
  }, []);

  const container = portalContainer ?? ownerBody;

  return (
    <>
      <span ref={markerRef} hidden data-slot='popover-portal-marker' />
      {container ? (
        <PopoverPrimitive.Portal container={container} forceMount={forceMount}>
          {children}
        </PopoverPrimitive.Portal>
      ) : null}
    </>
  );
}

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(
  (
    { className, side = 'bottom', sideOffset = 0, forceMount, ...props },
    ref,
  ) => {
    return (
      // The Portal has its own presence check, so it needs `forceMount` too or
      // it unmounts the content on close.
      <PopoverPortal forceMount={forceMount}>
        <PopoverPrimitive.Content
          ref={ref}
          forceMount={forceMount}
          data-slot='popover-content'
          side={side}
          sideOffset={sideOffset}
          className={cn(
            // Enter and exit motion comes from the caller's className.
            // Shares the modal layer, so whichever opened last paints on top: a
            // Modal opened from the panel always covers it.
            'z-modal outline-none origin-[--radix-popover-content-transform-origin]',
            className,
          )}
          {...props}
        />
      </PopoverPortal>
    );
  },
);

PopoverContent.displayName = PopoverPrimitive.Content.displayName;

/**
 * Page dim behind an open popover. It covers page content and docked bars
 * but sits under the Navbar layer, so the bar stays undimmed, and under the
 * panel's modal layer. Pointer presses on it dismiss the popover through
 * the usual outside-press handling.
 */
function PopoverBackdrop({
  className,
  open,
  ...props
}: React.ComponentPropsWithoutRef<'div'> & { open: boolean }) {
  const portalContainer = usePortalContainer();
  const contained = isContainedPreviewPortal(portalContainer);
  return (
    <PopoverPortal>
      <div
        data-slot='popover-backdrop'
        data-state={open ? 'open' : 'closed'}
        aria-hidden='true'
        className={cn(
          contained ? 'absolute' : 'fixed',
          'inset-0 z-backdrop',
          DIM_BACKDROP_CLASS,
          'motion-safe:data-[state=open]:animate-popover-fade-in',
          'motion-safe:data-[state=closed]:animate-popover-fade-out',
          className,
        )}
        {...props}
      />
    </PopoverPortal>
  );
}

const PopoverClose = PopoverPrimitive.Close;

export {
  Popover,
  PopoverAnchor,
  PopoverBackdrop,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
};
